import ValueTracker from "../../../../prop-trackers/value-tracker.js";
import DictTracker from "../../../../prop-trackers/dict-tracker.js";
import PropTracker from "../../../../prop-trackers/prop-tracker.js";
import SinglePropTracker from "../../../../prop-trackers/single-prop-tracker.js";
import TrackerGroup from "../../../../prop-trackers/tracker-group.js";
import Zipper from "../../../../prop-tracker-utils/zipper.js";
import { ForEach, ValueTracker as ForEachValueTracker } from "../../../../prop-tracker-utils/for-each.js";

class PbrMaterialPropertyTrackerAssigner {
    constructor(evtCtx, matTransAssigner) {
        this._materialDataTracker = new PropTracker();
        this._materialTracker = new PropTracker();

        this._zipper = new Zipper();
        this._zipper.setSource("data", this._materialDataTracker);
        this._zipper.setSource("material", this._materialTracker);

        this._pairTracker = new DictTracker();
        this._forEachTracker = new ForEach(this._pairTracker, (key) => new MaterialTracker(evtCtx, matTransAssigner));
    }

    trackMaterialData(materialData, materialDataEvents) {
        this._materialDataTracker.track(materialData, materialDataEvents);
    }

    trackMaterials(materials, materialEvents) {
        this._materialTracker.track(materials, materialEvents);
    }

    update() {
        this._zipper.updateIfNeeded(({ trackers }) => {
            for (const tracker of trackers) {
                tracker.update(({ userdata: matKey, props }) => {
                    const data = props.get("data").newValue;
                    const material = props.get("material").newValue;

                    if (data && material) {
                        this._pairTracker.setValue(matKey, { data, material });
                    } else {
                        this._pairTracker.deleteValue(matKey);
                    }
                });
            }
        });

        this._forEachTracker.updateIfNeeded(({ trackers }) => {
            for (const tracker of trackers) {
                tracker.update();
            }
        });
    }
}

class MaterialTracker extends ForEachValueTracker {
    constructor(evtCtx, matTransAssigner) {
        super();

        this._evtCtx = evtCtx;
        this._matTransAssigner = matTransAssigner;

        this._materialTracker = new ValueTracker();
        this._dataTracker = new ValueTracker();
        this._transpTracker = new SinglePropTracker();
        this._opTracker = new SinglePropTracker();
        this._prAlphaTracker = new SinglePropTracker();
        this._trackGroup = new TrackerGroup();

        const needsUpdate = () => this.protected_needsUpdate();
        this._materialTracker.listenNeedsUpdate(needsUpdate);
        this._dataTracker.listenNeedsUpdate(needsUpdate);
        this._trackGroup.listenNeedsUpdate(needsUpdate);

        this._trackGroup.add(this._transpTracker);
        this._trackGroup.add(this._opTracker);
        this._trackGroup.add(this._prAlphaTracker);

        this._material = null;
    }

    setValue(value) {
        let data, material;

        if (value) {
            ({ data, material } = value);
        }

        if (material) {
            this._materialTracker.setValue(material);
        } else {
            this._materialTracker.deleteValue();
        }

        if (data) {
            this._dataTracker.setValue(data);
        } else {
            this._dataTracker.deleteValue();
        }
    }

    deleteValue() {
        this._materialTracker.deleteValue();
        this._dataTracker.deleteValue();
    }

    protected_startUpdate() {
        const updateMaterial = this._materialTracker.needsUpdate;

        if (updateMaterial) {
            this._materialTracker.update(({ prop: { newValue: material } }) => {
                this._material = material || null;
            });
        }

        this._dataTracker.updateIfNeeded(({ prop: { newValue: data } }) => {
            data = data || null;
            const events = data ? this._evtCtx.getEvents(data) : null;

            this._transpTracker.track(data, events, "transparent");
            this._opTracker.track(data, events, "opacity");
            this._prAlphaTracker.track(data, events, "premultipliedAlpha");
        });

        if (updateMaterial || this._trackGroup.needsUpdate) {
            this._trackGroup.update(() => {
                const material = this._material;

                if (this._transpTracker.needsUpdate || this._opTracker.needsUpdate) {
                    this._transpTracker.update(({ prop: { newValue: transparent } }) => {
                        this._opTracker.update(({ prop: { newValue: opacity } }) => {
                            if (material) {
                                transparent = Boolean(transparent);
                                if (opacity === undefined || !transparent) opacity = 1;

                                this._matTransAssigner.setProperty(material, "transparent", transparent);
                                this._matTransAssigner.setProperty(material, "opacity", opacity);
                            }
                        });
                    });
                }

                this._prAlphaTracker.updateIfNeeded(({ prop: { newValue: premultipliedAlpha } }) => {
                    if (material) {
                        this._matTransAssigner.setProperty(material, "premultipliedAlpha", premultipliedAlpha);
                    }
                });
            });
        }
    }

    protected_finishUpdate() {}
}

export default PbrMaterialPropertyTrackerAssigner;
